import { transparentize } from 'polished';
import {
	ChangeEvent,
	Component,
	DragEvent,
	forwardRef,
	MouseEvent,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { IconAddPhotoPlus } from 'src/easyclaim.app.assets/icons/AddPhotoPlus';
import { BinIcon } from 'src/easyclaim.app.assets/icons/Bin';
import DocumentServiceContext from 'src/easyclaim.app.contexts/DocumentServiceContext';
import { dump, insert, load, remove, TagValues } from 'src/easyclaim.app.libs/piexif';
import { useContinueButtonStore } from 'src/easyclaim.app.stores/useContinueButtonStore';
import styled, { css } from 'styled-components';

import {
	DocumentAddCommandResult,
	EasyClaimCustomerConfigurationDto,
	EasyClaimDocumentType,
	ValidationResult,
	VehicleType,
} from '../../easyclaim.api.client.typescript/src/index';
import { IconAddPhoto, IconLoading, MagnifyingGlassIcon } from '../../easyclaim.app.assets/index';
import { useDeleteDocument } from '../../easyclaim.app.hooks/useDeleteDocument';
import { useIsPerformanceEnhancerEnabled } from '../../easyclaim.app.hooks/useIsPerformanceEnhancerEnabled';
import { useThumbnail } from '../../easyclaim.app.hooks/useThumbnail';
import { useUploadDocument } from '../../easyclaim.app.hooks/useUploadDocument';
import { cacheLegacyImageThumbnail } from '../../easyclaim.app.libs/cacheLegacyImageThumbnail';
import { isErrorReported, reportError } from '../../easyclaim.app.libs/reportError';
import { reportWarning } from '../../easyclaim.app.libs/reportWarning';
import {
	DocumentUploadError,
	UploadDocumentParams,
} from '../../easyclaim.app.services/DocumentService';
import {
	EasyClaimService,
	Image,
	ImageCompressionService,
	ImageContentReaderService,
	ImageSelectionService,
	SelectState,
	UploadDestination,
} from '../../easyclaim.app.services/index';
import { usePhotoThumbnailStore } from '../../easyclaim.app.stores/usePhotoThumbnailStore';
import theme from '../../easyclaim.app.theme/theme';
import { FadeIn } from '../animations/FadeIn';
import { FadeOut } from '../animations/FadeOut';
import { DeletePhotoConfirmationSlider } from '../modals/DeletePhotoConfirmationSlider';
import { EditImageModal } from '../modals/EditImageModal';

import { AddPhotoAreaButton } from './AddPhotoAreaButton';
import { type DeviceInfo, embedExifMetadata, getCurrentPosition } from './cameraExifUtils';
import { CameraWithOverlay } from './CameraWithOverlay';
import { CompanyCheck } from './CompanyCheck';
import { getEditIcon, TopRectangle } from './GetEditIcon';
import { FeedbackMode, ImageRecognitionFeedback2 } from './ImageRecognitionFeedback2';
import { pictureRejectionDocumentFeatures } from './PictureRejection';
import StatusIcon, { CenteredContainer } from './StatusIcon';
import styles from './styles/SelectImage.module.css';

export enum ImageFileInputType {
	Default = 'default',
	CapturePicture = 'capturePicture',
}

interface InputAdditionalSettings {
	accept?: string | undefined;
	capture?: 'user' | 'environment' | boolean | undefined;
}

interface SelectImageProps {
	componentId: string;
	imageFileInputType?: ImageFileInputType;
	caseId?: string | null;
	currentImage?: Image;
	src?: string;
	srcSet?: string;
	title: string;
	placeholder?: JSX.Element;
	marginBottom?: string;
	showEditIcon?: boolean;
	showStatusIcon?: boolean;
	photoIconSize?: 'Default' | 'Small';
	showUserFeedbackInfo?: boolean;
	className?: string;
	easyClaimService?: EasyClaimService;
	fileType: EasyClaimDocumentType;
	uploadDestination?: UploadDestination;
	configuration: EasyClaimCustomerConfigurationDto;
	disableUpdate?: boolean;
	isFileDeletionDisabled?: boolean;
	inputError?: boolean;
	canOverlayCamera?: boolean;
	vehicleType?: VehicleType;

	disableContainerClick?: boolean;

	onChange: (image?: Image) => void;

	onShowExample?: () => void;

	isExternallyLoading?: boolean;
	uploadDocumentMutation?: (params: UploadDocumentParams) => Promise<DocumentAddCommandResult>;
	deleteDocumentMutation?: (params: { caseId: string; documentId: string }) => Promise<void>;
}

interface SelectImageState {
	isChangeImageModalOpen: boolean;
	isDeletePhotoConfirmationOpen: boolean;
	uploadResult?: DocumentAddCommandResult;
	localizedConnectionErrorTitle?: string | null;
	localizedConnectionErrorDescription?: string | null;
	progress: number | null;
	isAnalyzing: boolean;
	isLoading: boolean;
	isCameraOpen: boolean;
	fileInvalidReasons: ('size' | 'extension')[];
	uploadedThumbnailUrl?: string;
	uploadedThumbnailForDocumentId?: string;
}

export class SelectImageBase extends Component<
	SelectImageProps & WrappedComponentProps,
	SelectImageState
> {
	private static readonly ACCEPTED_MIME_TYPES: string =
		'image/jpeg, image/pipeg, image/bmp, image/png, image/heic, image/heif';
	private static readonly VALID_FILE_EXTENSIONS: RegExp =
		/(\.jpg|\.jpeg|\.jfif|\.png|\.bmp|\.heic|\.heif)$/i;
	private static readonly MAX_FILE_SIZE: number = 104_857_600;
	private input?: HTMLInputElement | null;
	private imageCompressionService: ImageCompressionService;
	private imageContentReaderService: ImageContentReaderService;
	private captureCounter = 0;
	private isBusyRegistered = false;

	constructor(props: SelectImageProps & WrappedComponentProps) {
		super(props);

		this.imageContentReaderService = new ImageContentReaderService();

		this.imageCompressionService = new ImageCompressionService({
			maxHeight: 2048,
			maxWidth: 2048,
			quality: 0.8,
		});

		const stateCandidate: SelectImageState = {
			progress: null,
			isAnalyzing: false,
			isLoading: false,
			isChangeImageModalOpen: false,
			isDeletePhotoConfirmationOpen: false,
			uploadResult: props.currentImage ? props.currentImage.uploadResult : undefined,
			isCameraOpen: false,
			fileInvalidReasons: [],
		};

		if (this.props.easyClaimService) {
			const connectionErrorDetails = this.props.easyClaimService.getLocalizedConnectionError();

			stateCandidate.localizedConnectionErrorTitle = connectionErrorDetails.connectionErrorTitle;
			stateCandidate.localizedConnectionErrorDescription =
				connectionErrorDetails.connectionErrorDescription;
		} else {
			throw new Error('EasyClaimService is mandatory here');
		}

		this.state = stateCandidate;
	}

	static getDerivedStateFromProps(
		props: SelectImageProps,
		state: SelectImageState
	): SelectImageState {
		const newState = Object.assign({}, state, {
			uploadResult: props.currentImage ? props.currentImage.uploadResult : undefined,
		});

		return newState;
	}

	private isBusy(
		state: SelectImageState = this.state,
		props: SelectImageProps & WrappedComponentProps = this.props
	): boolean {
		return (
			state.isLoading ||
			state.isAnalyzing ||
			props.isExternallyLoading === true ||
			props.currentImage?.selectState === SelectState.Uploading ||
			props.currentImage?.selectState === SelectState.Reading
		);
	}

	private synchronizeBusyRegistration(): void {
		const isBusy = this.isBusy();

		if (isBusy === this.isBusyRegistered) {
			return;
		}

		this.isBusyRegistered = isBusy;

		if (isBusy) {
			useContinueButtonStore.getState().incrementBusy();
		} else {
			useContinueButtonStore.getState().decrementBusy();
		}
	}

	componentDidMount() {
		this.synchronizeBusyRegistration();
	}

	componentDidUpdate(
		_prevProps: SelectImageProps & WrappedComponentProps,
		_prevState: SelectImageState
	) {
		this.synchronizeBusyRegistration();
	}

	componentWillUnmount() {
		if (!this.isBusyRegistered) {
			return;
		}

		this.isBusyRegistered = false;
		useContinueButtonStore.getState().decrementBusy();
	}

	onContainerClick = (doNotOpenModalOnReplacingPicture: boolean) => {
		if (this.props.disableContainerClick) {
			return;
		}

		const canOverlayCamera =
			this.props.canOverlayCamera === true ||
			this.props.configuration?.canShowOverlayOnCamera === true;

		if (this.input) {
			if (
				this.props.currentImage?.selectState === undefined ||
				this.props.currentImage.selectState === SelectState.Init
			) {
				if (canOverlayCamera) {
					this.setState({ isCameraOpen: true });
				} else {
					this.input.click();
				}
			} else if (
				[
					SelectState.Read,
					SelectState.Uploaded,
					SelectState.Error,
					SelectState.UnprocessableEntity,
					SelectState.UnsupportedMediaType,
				].includes(this.props.currentImage?.selectState)
			) {
				if (doNotOpenModalOnReplacingPicture) {
					this.openCorrectedCamera(canOverlayCamera);
				} else {
					this.setState({ isChangeImageModalOpen: true });
				}
			}
		}
	};

	private deleteImageAndClear = async (): Promise<void> => {
		try {
			await this.deleteImage();
			this.props.onChange(undefined);
		} catch (error) {
			reportError(error, { context: 'SelectImage.deleteImageAndClear' });
		}
	};

	private deleteImageNow = (): void => {
		if (this.input) this.input.value = '';
		this.setState({ progress: null }, () => {
			void this.deleteImageAndClear();
		});
	};

	private requestImageDeletion = (e: MouseEvent<HTMLDivElement>): void => {
		e.stopPropagation();
		if (this.props.configuration.deletePhotoConfirmation === true) {
			this.setState({ isDeletePhotoConfirmationOpen: true });

			return;
		}

		this.deleteImageNow();
	};

	private confirmImageDeletion = (): void => {
		this.setState({ isDeletePhotoConfirmationOpen: false }, this.deleteImageNow);
	};

	private dataUrlToBlob = (dataUrl: string): Blob => {
		const parts = dataUrl.split(',');
		const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
		const byteString = atob(parts[1]);
		const ab = new ArrayBuffer(byteString.length);
		const ia = new Uint8Array(ab);

		for (let i = 0; i < byteString.length; i++) {
			ia[i] = byteString.charCodeAt(i);
		}

		return new Blob([ab], { type: mime });
	};

	private cacheUploadedThumbnail(newImage: Image, previousDocumentId?: string | null): boolean {
		if (!this.props.configuration?.canUseExperimentalPerformanceEnhancer) {
			return false;
		}
		const documentId = newImage.document?.documentId;

		if (previousDocumentId && previousDocumentId !== documentId) {
			usePhotoThumbnailStore.getState().clearThumbnail(previousDocumentId);
		}

		const fileContent = newImage.document?.fileContent;

		if (!documentId || !fileContent || !newImage.dataUrlHead) {
			return false;
		}
		try {
			const blob = this.dataUrlToBlob(`${newImage.dataUrlHead},${fileContent}`);

			usePhotoThumbnailStore.getState().setThumbnail(documentId, blob);
			const objectUrl = usePhotoThumbnailStore.getState().thumbnailsByImageId[documentId];

			if (objectUrl) {
				this.setState({
					uploadedThumbnailUrl: objectUrl,
					uploadedThumbnailForDocumentId: documentId,
				});

				return true;
			}
		} catch (e) {
			reportError(e, { context: 'SelectImage.cacheUploadedThumbnail' });
		}

		return false;
	}

	private releaseUploadedImageContent(newImage: Image, previousDocumentId?: string | null): void {
		if (!this.cacheUploadedThumbnail(newImage, previousDocumentId) || !newImage.document) {
			return;
		}

		newImage.document.fileContent = '';
		newImage.dataUrlHead = undefined;
	}

	private getDisplayImageSrc(): string | undefined {
		const documentId = this.props.currentImage?.document?.documentId;

		if (
			documentId &&
			this.props.configuration?.canUseExperimentalPerformanceEnhancer &&
			documentId === this.state.uploadedThumbnailForDocumentId &&
			this.state.uploadedThumbnailUrl
		) {
			return this.state.uploadedThumbnailUrl;
		}

		const fileContent = this.props.currentImage?.document?.fileContent;

		if (fileContent?.startsWith('blob:')) {
			return fileContent;
		}

		return ImageSelectionService.getImageAsDataUrl(this.props.currentImage);
	}

	private getCaptureFileName = (): string => {
		this.captureCounter++;
		const perspective = this.props.fileType;

		return `${perspective}-${this.captureCounter}.jpeg`;
	};

	private isUploadAttemptsExceeded(): boolean {
		return (
			!!this.props.currentImage &&
			!!this.props.currentImage.uploadResult &&
			!!this.props.currentImage.uploadResult.documentFeatureValues &&
			!!this.props.currentImage.uploadResult.documentFeatureValues.uploadAttemptsLimitExceeded
		);
	}

	private async embedExifData(imageSrc: string, trackInfo?: Partial<DeviceInfo>): Promise<string> {
		try {
			const position = await getCurrentPosition();

			return await embedExifMetadata(imageSrc, position, trackInfo);
		} catch (e) {
			reportWarning('Could not embed EXIF metadata', { error: e });

			return imageSrc;
		}
	}

	private extractExifBytes(imageDataUrl: string): string | null {
		try {
			const exifObj = load(imageDataUrl);

			if (exifObj && Object.keys(exifObj).length > 0) {
				return dump(exifObj);
			}
		} catch (e) {
			reportWarning('Could not extract EXIF from captured image', { error: e });
		}

		return null;
	}

	private stripExif(imageDataUrl: string): string {
		try {
			return remove(imageDataUrl);
		} catch {
			return imageDataUrl;
		}
	}

	private reEmbedExif(dataUrl: string, exifBytes: string | null): string {
		if (!exifBytes) return dataUrl;
		try {
			return insert(exifBytes, dataUrl);
		} catch (e) {
			reportWarning('Could not re-embed EXIF', { error: e });

			return dataUrl;
		}
	}

	private shouldCompressImage(): boolean {
		return (
			this.props.configuration?.compressImages === true ||
			this.props.configuration?.canUseExperimentalPerformanceEnhancer === true
		);
	}

	private async processImage(
		imageSrc: string,
		fileName: string,
		trackInfo?: Partial<DeviceInfo>
	): Promise<string> {
		let finalDataUrl = imageSrc;

		if (this.shouldCompressImage()) {
			const blob = this.dataUrlToBlob(imageSrc);
			const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

			finalDataUrl = await this.imageCompressionService.compressToDataUrl(file);
		}

		const imageWithExif = await this.embedExifData(finalDataUrl, trackInfo);
		const originalExifBytes = this.extractExifBytes(imageWithExif);
		const strippedImage = this.stripExif(imageWithExif);
		const correctedImage = await this.ensureLandscapeOrientation(strippedImage);

		return this.reEmbedExif(correctedImage, originalExifBytes);
	}

	private async uploadDocumentToBackend(
		caseId: string,
		params: {
			documentType: EasyClaimDocumentType;
			fileContent: string | null | undefined;
			fileName: string | null | undefined;
			documentId?: string | null;
		}
	): Promise<DocumentAddCommandResult | undefined> {
		this.setProgress(null);

		if (this.props.configuration?.canUseExperimentalPerformanceEnhancer) {
			this.setAnalyzing(true);
			try {
				if (!this.props.uploadDocumentMutation) {
					throw new Error('Experimental document upload mutation is unavailable');
				}

				const result = await this.props.uploadDocumentMutation({
					caseId,
					fileContent: params.fileContent ?? '',
					fileName: params.fileName ?? '',
					documentType: params.documentType,
					documentId: params.documentId ?? undefined,
				});

				this.setProgress(100);

				return result;
			} finally {
				this.setAnalyzing(false);
			}
		}

		return this.props.easyClaimService?.putDocument(
			caseId,
			params,
			this.setProgress,
			this.setAnalyzing
		);
	}

	private async uploadImage(
		newImage: Image,
		fileName: string,
		currentImage?: Image
	): Promise<void> {
		newImage.selectState = SelectState.Uploading;
		this.props.onChange(newImage);

		const uploadedDocument = await this.uploadDocumentToBackend(this.props.caseId!, {
			documentType: this.props.fileType,
			fileContent: newImage.document!.fileContent,
			fileName: fileName,
			documentId: currentImage?.document?.documentId,
		});

		newImage.document!.documentId = uploadedDocument?.documentId;
		newImage.document!.creationDate = uploadedDocument?.creationDate ?? undefined;
		this.releaseUploadedImageContent(newImage, currentImage?.document?.documentId);

		const treatAsError =
			!uploadedDocument ||
			!uploadedDocument.validationResults ||
			uploadedDocument.validationResults.some(
				(vr) => vr.validationResult === ValidationResult.Error
			);

		newImage.selectState = treatAsError ? SelectState.UnprocessableEntity : SelectState.Uploaded;
		newImage.uploadResult = uploadedDocument;
		newImage.document!.validationResults = uploadedDocument?.validationResults;

		if (!(this.props.disableUpdate === true)) {
			this.setState({ uploadResult: uploadedDocument, isAnalyzing: false });
		}
		this.props.onChange(newImage);
	}

	private getResponseStatus(error: unknown): number | undefined {
		if (error instanceof DocumentUploadError) {
			return error.responseStatus;
		}

		if (
			typeof error === 'object' &&
			error !== null &&
			'responseStatus' in error &&
			typeof error.responseStatus === 'number'
		) {
			return error.responseStatus;
		}

		return undefined;
	}

	private handleImageError(error: unknown, newImage: Image): void {
		if (!isErrorReported(error)) {
			reportError(error, {
				context: 'SelectImage.handleImageError',
				documentId: newImage.document?.documentId,
			});
		}
		const responseStatus = this.getResponseStatus(error);

		if (responseStatus === 413) {
			if (newImage.document) {
				newImage.document.fileContent = '';
			}
			newImage.dataUrlHead = undefined;
			this.setState({ fileInvalidReasons: ['size'], uploadResult: undefined });
			this.props.onChange(undefined);

			return;
		}

		newImage.selectState =
			responseStatus === 422
				? SelectState.UnprocessableEntity
				: responseStatus === 415
					? SelectState.UnsupportedMediaType
					: SelectState.Error;

		if (responseStatus === undefined || newImage.selectState === SelectState.Error) {
			newImage.uploadResult = {
				validationResults: [
					{
						title: this.state.localizedConnectionErrorTitle,
						description: this.state.localizedConnectionErrorDescription,
						validationResult: ValidationResult.Error,
					},
				],
			};
			newImage.selectState = SelectState.Error;
		}

		if (!(this.props.disableUpdate === true)) {
			this.setState({ uploadResult: newImage.uploadResult, progress: null });
		}

		if (this.input) this.input.value = '';
		this.props.onChange(newImage);
	}

	onCameraCapture = async (imageSrc: string, trackInfo?: Partial<DeviceInfo>) => {
		this.setState({ isCameraOpen: false, fileInvalidReasons: [] });

		if (this.isUploadAttemptsExceeded()) return;

		const currentImage = this.props.currentImage;
		const fileName = this.getCaptureFileName();

		const newImage: Image = {
			componentId: this.props.componentId,
			selectState: SelectState.Reading,
			document: {
				documentType: this.props.fileType,
				fileContent: '',
				fileName: fileName,
			},
		};

		this.props.onChange(newImage);

		try {
			const finalDataUrl = await this.processImage(imageSrc, fileName, trackInfo);

			let cameraFileInvalidReasons: ('size' | 'extension')[] = [];

			const blob = this.dataUrlToBlob(finalDataUrl);
			const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

			cameraFileInvalidReasons = this.getFileInvalidReasons(file);

			if (cameraFileInvalidReasons.length > 0) {
				this.setState({ fileInvalidReasons: cameraFileInvalidReasons });
				newImage.selectState = SelectState.Init;
				this.props.onChange(newImage);

				return;
			}

			const parts = finalDataUrl.split(',');

			newImage.document!.fileContent = parts[1];
			newImage.dataUrlHead = parts[0];

			if (this.props.caseId) {
				await this.uploadImage(newImage, fileName, currentImage);
			} else {
				newImage.selectState = SelectState.Init;
				this.props.onChange(newImage);
			}
		} catch (e: unknown) {
			this.handleImageError(e, newImage);
		}
	};

	private ensureLandscapeOrientation = (dataUrl: string): Promise<string> => {
		let exifOrientation = 1;

		try {
			const exifObj = load(dataUrl);
			const orientationValue = exifObj?.['0th']?.[TagValues.ImageIFD.Orientation] as
				| number
				| undefined;

			if (orientationValue) {
				exifOrientation = Number(orientationValue);
			}
		} catch (e) {
			reportWarning('Could not read EXIF orientation, defaulting to 1', { error: e });
		}

		return new Promise((resolve) => {
			const img = new window.Image();

			img.onload = () => {
				if (img.width >= img.height) {
					resolve(dataUrl);

					return;
				}

				const canvas = document.createElement('canvas');

				canvas.width = img.height;
				canvas.height = img.width;

				const ctx = canvas.getContext('2d');

				if (!ctx) {
					resolve(dataUrl);

					return;
				}

				ctx.translate(canvas.width / 2, canvas.height / 2);
				ctx.rotate(-Math.PI / 2);
				ctx.drawImage(img, -img.width / 2, -img.height / 2);

				const result = canvas.toDataURL('image/jpeg', 1);

				resolve(result);
			};
			img.onerror = () => {
				resolve(dataUrl);
			};
			img.src = dataUrl;
		});
	};

	isValidFile = (file: File) =>
		file.size <= SelectImageBase.MAX_FILE_SIZE &&
		SelectImageBase.VALID_FILE_EXTENSIONS.test(file.name);

	private getFileInvalidReasons = (file: File): ('size' | 'extension')[] => {
		const reasons: ('size' | 'extension')[] = [];

		if (!SelectImageBase.VALID_FILE_EXTENSIONS.test(file.name)) reasons.push('extension');
		if (file.size > SelectImageBase.MAX_FILE_SIZE) reasons.push('size');

		return reasons;
	};

	onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();

		const uploadAttemptsExceeded =
			!!this.props.currentImage &&
			!!this.props.currentImage.uploadResult &&
			!!this.props.currentImage.uploadResult.documentFeatureValues &&
			this.props.currentImage.uploadResult.documentFeatureValues.uploadAttemptsLimitExceeded;

		if (uploadAttemptsExceeded) {
			return;
		}

		const files = e.target.files;

		void this.loadFiles(files);
	};

	tryEmbedEXIFData = async (finalDataUrl: string): Promise<string> => {
		try {
			const position = await getCurrentPosition();

			return await embedExifMetadata(finalDataUrl, position);
		} catch (e) {
			reportWarning('Could not embed EXIF metadata', { error: e });

			return finalDataUrl;
		}
	};

	loadFiles = async (files: FileList | null) => {
		if (!files || files.length === 0) return;

		if (this.props.currentImage?.selectState !== undefined) {
			if (
				[SelectState.Read, SelectState.Reading, SelectState.Uploading].includes(
					this.props.currentImage?.selectState
				)
			) {
				return;
			}
		}

		const file = files[0];

		this.setState({ fileInvalidReasons: [] });

		const currentImage = this.props.currentImage;
		const newImage: Image = {
			componentId: this.props.componentId,
			selectState: SelectState.Init,
		};

		this.props.onChange(newImage);
		try {
			const canOverlayCamera =
				this.props.canOverlayCamera === true ||
				this.props.configuration?.canShowOverlayOnCamera === true;

			if (this.isValidFile(file)) {
				newImage.selectState = SelectState.Reading;
				this.props.onChange(newImage);

				if (this.shouldCompressImage()) {
					const compressedImage = await this.imageCompressionService.compressToDataUrl(file);
					let finalDataUrl = compressedImage;

					if (canOverlayCamera) {
						finalDataUrl = await this.tryEmbedEXIFData(finalDataUrl);
					}

					const parts = finalDataUrl.split(',');

					newImage.document = {
						documentType: this.props.fileType,
						fileContent: parts[1],
						fileName: file.name,
					};

					newImage.dataUrlHead = parts[0];
				} else {
					const imageDataUrl = await this.imageContentReaderService.getImageDataUrl(file);
					let finalDataUrl = `${imageDataUrl.DataUrlHead},${imageDataUrl.Basae64Content}`;

					if (canOverlayCamera) {
						finalDataUrl = await this.tryEmbedEXIFData(finalDataUrl);
					}

					const parts = finalDataUrl.split(',');

					newImage.document = {
						documentType: this.props.fileType,
						fileContent: parts[1],
						fileName: file.name,
					};

					newImage.dataUrlHead = parts[0];
				}

				if (this.props.caseId) {
					newImage.selectState = SelectState.Uploading;
					this.props.onChange(newImage);

					const uploadedDocument = await this.uploadDocumentToBackend(this.props.caseId, {
						documentType: this.props.fileType,
						fileContent: newImage.document.fileContent,
						fileName: file.name,
						documentId: currentImage?.document?.documentId,
					});

					newImage.document.documentId = uploadedDocument ? uploadedDocument.documentId : undefined;
					newImage.document.creationDate =
						!!uploadedDocument && !!uploadedDocument.creationDate
							? uploadedDocument.creationDate
							: undefined;
					this.releaseUploadedImageContent(newImage, currentImage?.document?.documentId);

					const treatAsError =
						!uploadedDocument ||
						!uploadedDocument.validationResults ||
						uploadedDocument.validationResults.some(
							(vr) => vr.validationResult === ValidationResult.Error
						);

					const newSelectState = treatAsError
						? SelectState.UnprocessableEntity
						: SelectState.Uploaded;

					newImage.selectState = newSelectState;
					newImage.uploadResult = uploadedDocument;
					newImage.document.validationResults = uploadedDocument?.validationResults;

					if (!(this.props.disableUpdate === true)) {
						this.setState({ uploadResult: uploadedDocument, isAnalyzing: false });
					}
					this.props.onChange(newImage);
				} else {
					newImage.selectState = SelectState.Init;
					this.props.onChange(newImage);
				}
			} else {
				this.setState({
					fileInvalidReasons: this.getFileInvalidReasons(file),
					uploadResult: undefined,
				});
				this.props.onChange(undefined);
			}
		} catch (error: unknown) {
			this.handleImageError(error, newImage);
		}
	};

	onDrop = (e: DragEvent<any>) => {
		e.preventDefault();

		const uploadAttemptsExceeded =
			!!this.props.currentImage &&
			!!this.props.currentImage.uploadResult &&
			!!this.props.currentImage.uploadResult.documentFeatureValues &&
			this.props.currentImage.uploadResult.documentFeatureValues.uploadAttemptsLimitExceeded;

		if (uploadAttemptsExceeded) {
			return;
		}

		void this.loadFiles(e.dataTransfer.files);
	};

	onDragOver = (e: DragEvent<any>) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'copy';
	};

	deleteImage = async (): Promise<void> => {
		this.setState({ progress: null });
		if (this.props.caseId && this.props.currentImage?.document?.documentId) {
			if (
				this.props.uploadDestination === undefined ||
				this.props.uploadDestination === UploadDestination.EasyClaimCase
			) {
				if (this.props.configuration?.canUseExperimentalPerformanceEnhancer) {
					if (!this.props.deleteDocumentMutation) {
						throw new Error('Experimental document deletion mutation is unavailable');
					}

					return await this.props.deleteDocumentMutation({
						caseId: this.props.caseId,
						documentId: this.props.currentImage.document.documentId,
					});
				}

				return await this.props.easyClaimService?.deleteDocument(
					this.props.caseId,
					this.props.currentImage.document.documentId
				);
			}
		}
	};

	private getImageFileInputsettings(
		imageFileInputType?: ImageFileInputType
	): InputAdditionalSettings {
		if (imageFileInputType === ImageFileInputType.CapturePicture) {
			return { accept: 'image/*', capture: true };
		} else {
			return { accept: SelectImageBase.ACCEPTED_MIME_TYPES };
		}
	}

	private setProgress = (percentage: number | null) => {
		this.setState({ progress: percentage });
	};

	private setAnalyzing = (isAnalyzing: boolean) => {
		this.setState({ isAnalyzing: isAnalyzing });
	};

	private openCorrectedCamera(canOverlayCamera: boolean): void | undefined {
		if (canOverlayCamera) {
			this.setState({ isCameraOpen: true });
		} else {
			this.input?.click();
		}
	}

	render() {
		const { intl } = this.props;
		const hasImageRecognitionUserFeedback = true;

		const useCaptureInputType = this.props.imageFileInputType
			? this.props.imageFileInputType
			: this.props.configuration.useFileInputCameraCapture
				? ImageFileInputType.CapturePicture
				: ImageFileInputType.Default;

		const imageFileInputsettings: InputAdditionalSettings = this.getImageFileInputsettings(
			this.props.imageFileInputType ?? useCaptureInputType
		);

		const showUserFeedbackInfo = this.props.showUserFeedbackInfo ?? true;
		const isUniqa = CompanyCheck.isUniqaPL(this.props.configuration.companyShortName);
		const useSolvdStyles = !!this.props.configuration?.useSolvdStyles;
		const doNotOpenModalOnReplacingPicture = isUniqa;
		const isFileDeletionDisabled =
			this.props.isFileDeletionDisabled ?? this.props.configuration.isFileDeletionDisabled === true;

		const isStatusIconVisible =
			(showUserFeedbackInfo &&
				(this.props.currentImage?.selectState === SelectState.Uploaded ||
					this.props.currentImage?.selectState === SelectState.Read ||
					this.props.currentImage?.selectState === SelectState.Uploading ||
					this.props.currentImage?.selectState === SelectState.UnprocessableEntity ||
					this.props.currentImage?.selectState === SelectState.Error)) ||
			this.props.currentImage?.selectState === SelectState.Reading;

		const isFeedbackVisible =
			this.state.fileInvalidReasons.length === 0 &&
			showUserFeedbackInfo &&
			(this.props.currentImage?.selectState === SelectState.Uploaded ||
				this.props.currentImage?.selectState === SelectState.Read ||
				this.props.currentImage?.selectState === SelectState.UnprocessableEntity ||
				this.props.currentImage?.selectState === SelectState.Error);

		const isUploading =
			this.props.currentImage?.selectState === SelectState.Uploading ||
			this.props.currentImage?.selectState === SelectState.Reading;

		const isEditIconVisible =
			!!this.props.currentImage &&
			!!this.props.currentImage.uploadResult &&
			!!this.props.currentImage.uploadResult.documentFeatureValues &&
			this.props.currentImage.uploadResult.documentFeatureValues.uploadAttemptsLimitExceeded
				? false
				: this.props.showEditIcon !== false &&
					this.props.currentImage &&
					[
						SelectState.Uploaded,
						SelectState.Error,
						SelectState.UnprocessableEntity,
						SelectState.UnsupportedMediaType,
					].includes(this.props.currentImage.selectState);

		const { document } = this.props.currentImage || {};
		const lockedDocTypes: EasyClaimDocumentType[] = [
			EasyClaimDocumentType.RegistrationDocument,
			EasyClaimDocumentType.RegistrationDocumentInside,
			EasyClaimDocumentType.DniInside,
			EasyClaimDocumentType.DniOutside,
			EasyClaimDocumentType.VehicleDatasheetInside,
			EasyClaimDocumentType.VehicleDatasheetOutside,
			EasyClaimDocumentType.DriverLicenseInside,
			EasyClaimDocumentType.DriverLicenseOutside,
			EasyClaimDocumentType.ITV,
		];
		const isLockedDocType =
			document?.documentType && lockedDocTypes.includes(document.documentType);

		const isLocked =
			(this.props.currentImage?.document?.fileContent === 'data:' ||
				this.props.currentImage?.document?.fileContent === '') &&
			isLockedDocType;

		const documentIsRejected = this.props.currentImage?.document?.validationResults?.some((vr) =>
			pictureRejectionDocumentFeatures.some((e) => e === vr.feature)
		);

		const isEmpty =
			!this.props.currentImage ||
			this.props.currentImage.selectState === undefined ||
			this.props.currentImage.selectState === SelectState.Init;
		const showInputError = isEmpty && this.props.inputError === true;
		const canScroll = this.props.configuration?.canScrollToMissingPhoto === true;
		const showMissingPhotoFeedback = canScroll && showInputError;

		const clickOnSelectImagePhoto = () => {
			if (this.props.configuration.showUpdatedVerificationResults) return;

			return this.onContainerClick(doNotOpenModalOnReplacingPicture);
		};

		return (
			<Container className="select-image-container" marginBottom={this.props.marginBottom}>
				<ImageContainer
					className={`select-image-photo ${
						this.props.className !== undefined ? this.props.className : ''
					}${
						documentIsRejected ? ' select-image-imageRejected' : ''
					}${showInputError ? ' image-upload-error' : ''}`}
					onClick={() => {
						clickOnSelectImagePhoto();
					}}
					onDrop={this.onDrop}
					onDragOver={this.onDragOver}
					borderRadius={hasImageRecognitionUserFeedback ? '3px 3px 0px 0px' : '3px'}
				>
					{!this.props.currentImage?.document?.fileContent && this.props.src && (
						<>
							<Img
								srcSet={this.props.srcSet}
								src={this.props.src}
								alt="document"
								borderRadius={hasImageRecognitionUserFeedback ? '3px 3px 0px 0px' : '3px'}
							/>
						</>
					)}
					{(this.props.currentImage?.document?.fileContent || isLocked) && (
						<>
							<Img
								className={
									isLocked
										? `select-image-locked-${this.props.currentImage?.document?.documentType}`
										: ''
								}
								src={this.getDisplayImageSrc()}
								alt={
									this.props.currentImage?.document?.documentType?.toString() ||
									'document with unknown type'
								}
								borderRadius={hasImageRecognitionUserFeedback ? '3px 3px 0px 0px' : '3px'}
							/>
							{this.props.configuration?.showUpdatedVerificationResults &&
								!isFileDeletionDisabled &&
								!isLocked &&
								!isUploading && (
									<div className={styles.deleteImageButton} onClick={this.requestImageDeletion}>
										<BinIcon />
									</div>
								)}
						</>
					)}
					{isLocked && document && (
						<div className="select-image-locked-text">
							<FormattedMessage id={`image.locked.${document.documentType?.toString()}`} />
						</div>
					)}
					{this.props.currentImage?.selectState !== undefined && (
						<Overlay
							isVisible={[SelectState.Uploading].includes(this.props.currentImage?.selectState)}
							borderRadius={hasImageRecognitionUserFeedback ? '3px 3px 0px 0px' : '3px'}
						/>
					)}
					{isStatusIconVisible && !this.state.isAnalyzing ? (
						<StatusIcon uploadResult={this.state.uploadResult} isUploading={isUploading} />
					) : null}
					{!(this.props.src || this.props.currentImage?.document?.fileContent || isLocked) &&
						this.props.placeholder &&
						this.props.placeholder}
					{isEditIconVisible &&
						!isUniqa &&
						getEditIcon(() => this.setState({ isChangeImageModalOpen: true }))}
					{isEditIconVisible && isUniqa && (
						<TopRectangle className="change-photo-button">
							<AddPhotoAreaButton textId="button.backchangeImage" />
						</TopRectangle>
					)}
					{this.props.currentImage?.selectState === undefined && !isUniqa && (
						<>
							<StyledIconAddPhoto
								className="AddPhotoIcon"
								size={this.props.photoIconSize}
								onDragOver={this.onDragOver}
							/>
							<StyledIconAddPhotoPlus className="AddPhotoPlusIcon" onDragOver={this.onDragOver} />
						</>
					)}
					{this.props.currentImage?.selectState === undefined && isUniqa && (
						<>
							<AddPhotoComponent>
								<AddPhotoAreaButton textId="fourDiagonalPerspectives.addPhoto" />
							</AddPhotoComponent>
						</>
					)}

					{(this.state.isLoading === true || this.props.isExternallyLoading === true) && (
						<CenteredContainer>
							<div className="solvdLoadingContainer">
								<IconLoading className="solvdLoading" />
								{useSolvdStyles ? <h2>{<FormattedMessage id="photoLoading.title" />}</h2> : null}
							</div>
						</CenteredContainer>
					)}

					{this.state.isAnalyzing === true &&
						(useSolvdStyles ? (
							<CenteredContainer>
								<div className="solvdLoadingContainer">
									<IconLoading className="solvdLoading" />
									<h2>{<FormattedMessage id="photoAnalyzing.title" />}</h2>
								</div>
							</CenteredContainer>
						) : (
							<MagnifyingGlassIcon className="loop" />
						))}

					{!!this.state.uploadResult &&
					!!this.state.uploadResult.documentFeatureValues &&
					this.state.uploadResult.documentFeatureValues.uploadAttemptsLimitExceeded ? null : (
						<Input
							ref={(r) => (this.input = r)}
							onClick={(e) => e.stopPropagation()}
							onChange={this.onFileChange}
							{...imageFileInputsettings}
						/>
					)}
					<EditImageModal
						isOpen={this.state.isChangeImageModalOpen}
						src={this.props.src}
						scrSet={this.props.srcSet}
						image={this.getDisplayImageSrc()}
						title={this.props.title}
						onSelect={() => {
							const canOverlayCamera =
								this.props.canOverlayCamera === true ||
								this.props.configuration?.canShowOverlayOnCamera === true;

							this.setState(
								{
									isChangeImageModalOpen: false,
								},
								() => {
									if (canOverlayCamera) {
										this.setState({ isCameraOpen: true });
									} else {
										this.input?.click();
									}
								}
							);
						}}
						onDelete={
							isFileDeletionDisabled
								? undefined
								: () => {
										if (this.input) this.input.value = '';

										this.setState(
											{
												progress: null,
												isChangeImageModalOpen: false,
											},
											() => {
												void this.deleteImageAndClear();
											}
										);
									}
						}
						onClose={() => this.setState({ isChangeImageModalOpen: false })}
					/>
					<DeletePhotoConfirmationSlider
						isOpen={this.state.isDeletePhotoConfirmationOpen}
						onClose={() => this.setState({ isDeletePhotoConfirmationOpen: false })}
						onConfirm={this.confirmImageDeletion}
					/>
				</ImageContainer>

				{!!this.state.progress && (
					<div className="w3-light-grey">
						<div className="w3-progressBar" style={{ width: `${this.state.progress}%` }} />
					</div>
				)}
				{isFeedbackVisible ? (
					<ImageRecognitionFeedback2
						validationId={this.props.componentId}
						uploadResult={this.state.uploadResult}
						onShowExample={this.props.onShowExample}
						onChangeImage={() =>
							this.openCorrectedCamera(
								this.props.canOverlayCamera === true ||
									this.props.configuration?.canShowOverlayOnCamera === true
							)
						}
						feedbackMode={FeedbackMode.Main}
						groupFeedback={isUniqa}
					/>
				) : null}
				{showMissingPhotoFeedback && (
					<ImageRecognitionFeedback2
						validationId={this.props.componentId}
						uploadResult={{
							validationResults: [
								{
									validationResult: ValidationResult.Error,
									title: intl.formatMessage({ id: 'missingPhoto.title' }),
									description: intl.formatMessage({ id: 'missingPhoto.description' }),
								},
							],
						}}
						onShowExample={this.props.onShowExample}
						onChangeImage={() =>
							this.openCorrectedCamera(
								this.props.canOverlayCamera === true ||
									this.props.configuration?.canShowOverlayOnCamera === true
							)
						}
						feedbackMode={FeedbackMode.Main}
						groupFeedback={isUniqa}
					/>
				)}
				<CameraWithOverlay
					isOpen={this.state.isCameraOpen}
					fileType={this.props.fileType}
					vehicleType={this.props.vehicleType}
					onCapture={(imageSrc, trackInfo) => {
						void this.onCameraCapture(imageSrc, trackInfo);
					}}
					onClose={() => this.setState({ isCameraOpen: false })}
				/>
				{this.state.fileInvalidReasons.length > 0 && (
					<ImageRecognitionFeedback2
						validationId={this.props.componentId}
						uploadResult={{
							validationResults: this.state.fileInvalidReasons.map((reason) => ({
								validationResult: ValidationResult.Error,
								title: intl.formatMessage({ id: `invalidFile.${reason}.title` }),
								description: intl.formatMessage({ id: `invalidFile.${reason}.description` }),
							})),
						}}
						onShowExample={this.props.onShowExample}
						onChangeImage={() =>
							this.openCorrectedCamera(
								this.props.canOverlayCamera === true ||
									this.props.configuration?.canShowOverlayOnCamera === true
							)
						}
						feedbackMode={FeedbackMode.Main}
						groupFeedback={isUniqa}
					/>
				)}
			</Container>
		);
	}
}

function isShellImage(image: Image | undefined): boolean {
	return !!image?.document?.documentId && !image.document.fileContent;
}

const SelectImageWithQuery = forwardRef<SelectImageBase, SelectImageProps & WrappedComponentProps>(
	function SelectImageWithQuery(props, ref) {
		const { currentImage, caseId, componentId, onChange } = props;
		const documentService = useContext(DocumentServiceContext);
		const isPerformanceEnhancerEnabled = useIsPerformanceEnhancerEnabled();
		const uploadMutation = useUploadDocument();
		const deleteMutation = useDeleteDocument();
		const [legacyImage, setLegacyImage] = useState<Image>();
		const [isFetchingLegacyImage, setIsFetchingLegacyImage] = useState(false);

		const docId = currentImage?.document?.documentId;
		const isShell = isShellImage(currentImage);

		const {
			thumbnail,
			isLoading: isFetchingThumbnail,
			shouldFallbackToLegacyImage,
		} = useThumbnail({
			caseId: caseId ?? undefined,
			imageId: docId ?? undefined,
			enabled: isShell,
		});

		const canUseThumbnailPreview = isShell && !!thumbnail && !!currentImage?.document;
		const shouldFetchLegacyImage = !isPerformanceEnhancerEnabled || shouldFallbackToLegacyImage;

		useEffect(() => {
			if (
				!shouldFetchLegacyImage ||
				!documentService ||
				!caseId ||
				!docId ||
				!currentImage?.document ||
				!isShell
			) {
				setLegacyImage(undefined);
				setIsFetchingLegacyImage(false);

				return;
			}

			let isCancelled = false;

			setIsFetchingLegacyImage(true);
			documentService
				.getImageForDocument(caseId, docId, currentImage.document, componentId)
				.then((image) => {
					if (!isCancelled) {
						setLegacyImage(image);
						void cacheLegacyImageThumbnail(docId, image).catch((error: unknown) => {
							reportError(error, {
								context: 'SelectImage.cacheLegacyImageThumbnail',
								caseId,
								imageId: docId,
								componentId,
							});
						});
					}
				})
				.catch((error: unknown) => {
					if (!isCancelled) {
						reportError(error, {
							context: 'SelectImage.fetchLegacyImage',
							caseId,
							imageId: docId,
							componentId,
						});
					}
				})
				.finally(() => {
					if (!isCancelled) {
						setIsFetchingLegacyImage(false);
					}
				});

			return () => {
				isCancelled = true;
			};
		}, [
			shouldFetchLegacyImage,
			documentService,
			caseId,
			docId,
			currentImage?.document,
			componentId,
			isShell,
		]);

		useEffect(() => {
			if (!shouldFetchLegacyImage || !legacyImage || !isShell) {
				return;
			}
			onChange(legacyImage);
		}, [legacyImage, isShell, shouldFetchLegacyImage, onChange]);

		useEffect(() => {
			if (!isPerformanceEnhancerEnabled || !thumbnail || !isShell || !currentImage?.document) {
				return;
			}
			onChange({ ...currentImage, document: { ...currentImage.document, fileContent: thumbnail } });
		}, [thumbnail, isShell, isPerformanceEnhancerEnabled, currentImage, onChange]);

		const handleChange = useCallback(
			(image?: Image) => {
				onChange(image);
			},
			[onChange]
		);

		const effectiveImage = canUseThumbnailPreview
			? { ...currentImage, document: { ...currentImage.document, fileContent: thumbnail } }
			: shouldFetchLegacyImage && isShell && legacyImage
				? legacyImage
				: currentImage;

		const isExternallyLoading =
			isShell &&
			!canUseThumbnailPreview &&
			(isFetchingThumbnail || (shouldFetchLegacyImage && isFetchingLegacyImage));

		return (
			<SelectImageBase
				{...props}
				ref={ref}
				currentImage={effectiveImage}
				onChange={handleChange}
				isExternallyLoading={isExternallyLoading}
				uploadDocumentMutation={(params) => uploadMutation.mutateAsync(params)}
				deleteDocumentMutation={(params) => deleteMutation.mutateAsync(params)}
			/>
		);
	}
);

export const SelectImage = injectIntl(SelectImageWithQuery, { forwardRef: true });

const Input = styled.input.attrs({ type: 'file' })`
	display: none;
	visibility: hidden;
`;

const Overlay = styled.div<{ isVisible: boolean; borderRadius: string }>`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	z-index: 999;
	background: ${(p) => transparentize(0.5, (p.theme as typeof theme).colors.text.secondary)};
	overflow: hidden;
	animation: ${(p) =>
			p.isVisible
				? ''
				: css`
						${FadeOut}
					`}
		0.6s ease-in forwards;
	border-radius: ${(p) => p.borderRadius};
	transition: border-radius 1.2s ease-out;
`;

const Container = styled.div<{ marginBottom?: string }>`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	flex-grow: 0;
	margin: 0 0 ${(p) => p.marginBottom || '0'} 0;
`;

const ImageContainer = styled.div<{ borderRadius: string }>`
	position: relative;
	background-color: ${(p) => transparentize(0.7, (p.theme as typeof theme).colors.secondary)};
	box-sizing: content-box;
	max-width: 360px;
	cursor: pointer;
	border-radius: ${(p) => p.borderRadius};
	transition: border-radius 1.2s ease-out;
	min-height: 4rem;
`;

const Img = styled.img<{ borderRadius: string }>`
	display: block;
	width: 100%;
	border-radius: ${(p) => p.borderRadius};
	transition: border-radius 1.2s ease-out;
	min-height: 8rem;
	animation: ${css`
			${FadeIn}`} 0.6s forwards;
`;

const StyledIconAddPhoto = styled(IconAddPhoto)<{ size?: 'Default' | 'Small' }>`
	color: rgba(0, 0, 0, 0.8);
	position: absolute;
	top: 50%;
	left: 50%;
	height: ${(p) => (p.size === 'Small' ? '3rem' : '4rem')};
	transform: translate(-50%, -50%);
`;

const AddPhotoComponent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	// background-color: rgba(255, 255, 255, 255);
	// color: #005CA9;
	// font-weight: bold;
	// border-radius: 5px;
	// height: 3em;

	// > div:first-child {
	//     display: flex;
	//     justify-content: center;
	//     align-items: center;
	//     font-size: 2.5em;
	//     width: 1em;
	//     height: 100%;
	//     border-radius: 5px;
	// }

	// > div:last-child {
	//     display: flex;
	//     align-items: center;
	//     margin: 0.5em;
	// 	margin-left: 0em;
	//     height: 100%;
	// }
`;

const StyledIconAddPhotoPlus = styled(IconAddPhotoPlus)`
	border-radius: 50%;
	border-style: solid;
	border-width: 2px;
	border-color: var(--primary-color);
	color: var(--primary-color);
	padding: 2px;

	background-color: #8dc1e9;
	height: 1rem;
	width: 1rem;

	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;
